import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/people.css';
import './people.css';

const ICN = {
    search: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    caret: (
        <svg className="caret" viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    plus: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    vGrid: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="2" y="2" width="4" height="4" rx="0.8" stroke="currentColor" strokeWidth="1.5" />
            <rect x="8" y="2" width="4" height="4" rx="0.8" stroke="currentColor" strokeWidth="1.5" />
            <rect x="2" y="8" width="4" height="4" rx="0.8" stroke="currentColor" strokeWidth="1.5" />
            <rect x="8" y="8" width="4" height="4" rx="0.8" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
    vTable: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="2" y="3" width="10" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
            <path d="M2 6h10M2 9h10M6 3v8" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
    invite: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="6" cy="5" r="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M2 12c.6-2 2-3 4-3s3.4 1 4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M10 3h3M11.5 1.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    more: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="3" cy="7" r="1" fill="currentColor" />
            <circle cx="7" cy="7" r="1" fill="currentColor" />
            <circle cx="11" cy="7" r="1" fill="currentColor" />
        </svg>
    ),
    x: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
};

const VIEWS = ['Everyone', 'Members', 'Translators', 'Reviewers', 'Pending invites', 'Suspended'];

const ROLES = ['Any role', 'Admin', 'Localization PM', 'Engineer', 'Translator', 'Reviewer', 'Designer', 'Viewer'];
const STATES = ['Any state', 'Online', 'Away', 'Offline', 'Invited'];

const initials = (name) => (name || '?')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const avPal = (idx) => `av-${(idx % 6) + 1}`;

const normalizeMembers = (raw) => raw.map((m, i) => {
    const u = m.userId || m.user || m;
    const status = m.invitationStatus === 'pending' ? 'invite' : (m.status || (m.online ? 'online' : 'offline'));
    return {
        id: u._id || m._id || `m-${i}`,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || `Member ${i + 1}`,
        email: u.email,
        initials: initials(`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email),
        av: avPal(i),
        role: m.role || (m.translatedLanguages ? 'Translator' : 'Member'),
        kind: m.invitationStatus === 'pending' ? 'invite' : (m.role && m.role.toLowerCase().includes('admin') ? 'member' : 'external'),
        langs: u.translatedLanguages || [],
        teams: m.teams || [],
        workload: u.workloadCapacity || m.workload || 0,
        projects: m.assignedProjectsCount || 0,
        status,
        last: m.lastActive ? new Date(m.lastActive).toLocaleString() : (status === 'invite' ? 'Invited' : ''),
        joined: u.createdAt ? new Date(u.createdAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '',
        rate: u.rate || '',
        quality: u.performanceStats && u.performanceStats.qualityScore,
        perms: m.role || 'Member',
    };
});

const Views = ({ active, setActive, counts }) => (
    <div className="rs-views">
        {VIEWS.map((name) => (
            <button
                key={name}
                type="button"
                className={`rs-view-tab ${active === name ? 'on' : ''}`}
                onClick={() => setActive(name)}
            >
                {name}
                <span className="ct">{counts[name] || 0}</span>
            </button>
        ))}
        <button type="button" className="rs-view-tab add">+ Save view</button>
    </div>
);

const Toolbar = ({ q, setQ, view, setView, facets, cycleFacet, clearFacets }) => {
    const anyApplied = Object.values(facets).some((v) => v && !v.startsWith('Any'));
    return (
        <div className="rs-toolbar">
            <div className="rs-tb-search">
                {ICN.search}
                <input placeholder="Search by name, email, language…" value={q} onChange={(e) => setQ(e.target.value)} />
                <span className="kbd">/</span>
            </div>
            <button type="button" className="rs-tb-facet" onClick={() => cycleFacet('role', ROLES)}>
                <span className="lbl-k">Role:</span><span className="lbl-v">{facets.role}</span>{ICN.caret}
            </button>
            <button type="button" className="rs-tb-facet" onClick={() => cycleFacet('state', STATES)}>
                <span className="lbl-k">State:</span><span className="lbl-v">{facets.state}</span>{ICN.caret}
            </button>
            {anyApplied && (
                <button type="button" className="rs-tb-facet" onClick={clearFacets} style={{ color: 'var(--ink-3)' }}>
                    {ICN.x}<span style={{ fontWeight: 500 }}>Clear</span>
                </button>
            )}
            <div className="rs-tb-spacer" />
            <div className="rs-tb-view">
                <button type="button" className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')} aria-label="Grid">{ICN.vGrid}</button>
                <button type="button" className={view === 'table' ? 'on' : ''} onClick={() => setView('table')} aria-label="Table">{ICN.vTable}</button>
            </div>
        </div>
    );
};

const statusLabel = (s) => ({
    online: 'Online',
    away: 'Away',
    offline: 'Offline',
    invite: 'Invited',
}[s] || 'Offline');

const PersonCard = ({ person, navigate }) => (
    <button
        type="button"
        className={`ppl-card status-${person.status}`}
        onClick={() => navigate(`/dashboard/chat/${person.id}`)}
    >
        <div className="ppl-card-head">
            <span className={`av-s ${person.av}`}>{person.initials}</span>
            <div className="meta">
                <b>{person.name}</b>
                <span>{person.email || person.role}</span>
            </div>
        </div>
        <div className="ppl-card-row">
            <span className="ppl-role">{person.role}</span>
            <span className={`ppl-status ${person.status}`}>{statusLabel(person.status)}</span>
        </div>
        {person.langs && person.langs.length > 0 && (
            <div className="ppl-langs">
                {person.langs.slice(0, 5).map((l, i) => (
                    <span key={i} className="ppl-lang">{(l.code || l).toUpperCase()}</span>
                ))}
                {person.langs.length > 5 && <span className="ppl-lang more">+{person.langs.length - 5}</span>}
            </div>
        )}
        <div className="ppl-card-foot">
            <span className="proj">{person.projects} project{person.projects === 1 ? '' : 's'}</span>
            {person.workload > 0 && (
                <span className="cap">
                    <span className="bar"><span style={{ width: `${Math.min(100, person.workload)}%` }} /></span>
                </span>
            )}
        </div>
    </button>
);

const PeopleTable = ({ rows, navigate }) => (
    <div className="rs-card flat">
        <div className="ppl-row head">
            <span>Person</span>
            <span>Role</span>
            <span>Languages</span>
            <span>Workload</span>
            <span>Projects</span>
            <span>Status</span>
            <span />
        </div>
        {rows.map((p, i) => (
            <button
                type="button"
                key={p.id || i}
                className={`ppl-row body status-${p.status}`}
                onClick={() => navigate(`/dashboard/chat/${p.id}`)}
            >
                <div className="ppl-person">
                    <span className={`av-s ${p.av}`}>{p.initials}</span>
                    <span className="nm-wrap">
                        <b>{p.name}</b>
                        <span>{p.email}</span>
                    </span>
                </div>
                <span className="ppl-role">{p.role}</span>
                <div className="ppl-langs">
                    {(p.langs || []).slice(0, 4).map((l, idx) => (
                        <span key={idx} className="ppl-lang">{(l.code || l).toUpperCase()}</span>
                    ))}
                </div>
                <div className="ppl-cap">
                    <span className="bar"><span style={{ width: `${Math.min(100, p.workload || 0)}%` }} /></span>
                </div>
                <span className="ppl-proj">{p.projects}</span>
                <span className={`ppl-status ${p.status}`}>{statusLabel(p.status)}</span>
                <span className="more">{ICN.more}</span>
            </button>
        ))}
    </div>
);

const People = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeView, setActiveView] = useState('Everyone');
    const [q, setQ] = useState('');
    const [view, setView] = useState('grid');
    const [facets, setFacets] = useState({ role: 'Any role', state: 'Any state' });

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/team', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setMembers(normalizeMembers(Array.isArray(r.data) ? r.data : []));
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load team.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const cycleFacet = (k, opts) => {
        setFacets((f) => {
            const i = opts.indexOf(f[k]);
            return { ...f, [k]: opts[(i + 1) % opts.length] };
        });
    };

    const clearFacets = () => setFacets({ role: 'Any role', state: 'Any state' });

    const counts = useMemo(() => ({
        Everyone: members.length,
        Members: members.filter((m) => m.kind === 'member').length,
        Translators: members.filter((m) => /translator/i.test(m.role)).length,
        Reviewers: members.filter((m) => /reviewer/i.test(m.role)).length,
        'Pending invites': members.filter((m) => m.kind === 'invite').length,
        Suspended: members.filter((m) => m.status === 'suspended').length,
    }), [members]);

    const filtered = useMemo(() => {
        let r = members;
        if (activeView === 'Members') r = r.filter((m) => m.kind === 'member');
        else if (activeView === 'Translators') r = r.filter((m) => /translator/i.test(m.role));
        else if (activeView === 'Reviewers') r = r.filter((m) => /reviewer/i.test(m.role));
        else if (activeView === 'Pending invites') r = r.filter((m) => m.kind === 'invite');
        else if (activeView === 'Suspended') r = r.filter((m) => m.status === 'suspended');
        if (facets.role !== 'Any role') r = r.filter((m) => m.role === facets.role);
        if (facets.state !== 'Any state') r = r.filter((m) => statusLabel(m.status) === facets.state);
        const s = q.trim().toLowerCase();
        if (s) r = r.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(s));
        return r;
    }, [members, activeView, facets, q]);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading team…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">People</h1>
                <span className="rs-page-title-count">{members.length}</span>
                <div className="rs-page-actions">
                    <button type="button" className="btn-p" onClick={() => navigate('/dashboard/team')}>{ICN.invite}<span>Invite people</span></button>
                </div>
            </div>
            <p className="rs-page-sub">Everyone in your workspace — members, translators, reviewers, and pending invites.</p>

            <Views active={activeView} setActive={setActiveView} counts={counts} />
            <Toolbar q={q} setQ={setQ} view={view} setView={setView} facets={facets} cycleFacet={cycleFacet} clearFacets={clearFacets} />

            {filtered.length === 0 ? (
                <div className="rs-card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>No people match</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>Try clearing filters or switching views.</div>
                </div>
            ) : view === 'grid' ? (
                <div className="ppl-grid">
                    {filtered.map((p, i) => <PersonCard key={p.id || i} person={p} navigate={navigate} />)}
                </div>
            ) : (
                <PeopleTable rows={filtered} navigate={navigate} />
            )}
        </>
    );
};

export default People;
